// ───────────────────────────────────────────────
// Per-round summary card + shot heatmap.
//
// Shared by Home's "Recent results" and Practice's "Recent sessions" —
// both render the last few rounds as a card with a shot-position heatmap
// and the round's headline stats. Fed a full RoundDetail (ends + arrows),
// since the heatmap needs per-arrow x/y.
// ───────────────────────────────────────────────
import { Link } from "react-router-dom";
import { TargetFaceLazy as TargetFace } from "./TargetFaceLazy";
import { formatDayMonthYearUpper } from "../lib/dates";
import { oneDecimal } from "../lib/numbers";
import { toVbX, toVbY } from "../lib/targetGeometry";

// `roundStats(round)` used to live here — a per-arrow derivation superseded by
// summaryStats() below, which reads the server-computed summary instead. It had
// no callers left outside its own tests, and its `x_count` fallback was the last
// inline `filter(a => a.score === "X")` in the app. Deleted in #2068.

/**
 * A small target thumbnail with a translucent dot per arrow — overlapping
 * dots accumulate into a heat scatter. Renders the actual recorded face so
 * indoor40, vegas, nfaa5spot etc. each look visually distinct.
 */
export function ShotHeatmap({ arrows = [], face = "wa40", size = 84 }) {
  const placed = arrows.filter((a) => a.x != null && a.y != null);
  return (
    <div
      className="heatmap anchor-rel"
      style={{ width: size, height: size }}
      role="img"
      aria-label={`Shot heatmap — ${placed.length} arrows`}
    >
      <TargetFace face={face} size={size} />
      {/* Arrow dots overlaid at the same viewBox scale as the face SVG */}
      <svg
        viewBox="0 0 240 240"
        className="heatmap-dots"
        style={{ width: size, height: size }}
        aria-hidden="true"
      >
        {placed.map((a, i) => (
          <circle
            key={i}
            className="hm-shot"
            cx={toVbX(a.x)}
            cy={toVbY(a.y)}
            r="7"
            fill="var(--nock)"
            stroke="var(--dot-stroke-light)"
            strokeWidth="1.2"
          />
        ))}
      </svg>
    </div>
  );
}

// Thumbnail shown in place of the shot heatmap for rounds the viewer only
// scored (didn't shoot). A clipboard marks it as a scored-not-shot round.
export function ScoredThumbnail({ size = 84 }) {
  return (
    <div
      className="heatmap rsc-scored-thumb"
      style={{ width: size, height: size }}
      role="img"
      aria-label="You scored this round"
    >
      <span className="rsc-scored-icon" aria-hidden="true">📋</span>
    </div>
  );
}

// Stats derivable from an enriched summary (no arrow objects). Both the History
// list (compact) AND the Home/Practice cards feed these — total, X-count and the
// 10/9 ring counts come straight off the server-computed summary (#1310), and
// the per-arrow average is derived from ends_done × arrows_per_end. The shot
// heatmap reads the summary's `shot_positions` directly, so neither card needs a
// per-round RoundDetail fetch.
function summaryStats(round) {
  const total = round.total ?? 0;
  const xCount = round.x_count ?? 0;
  const tens = round.ten_count ?? 0;
  const nines = round.nine_count ?? 0;
  const shot = (round.ends_done ?? 0) * (round.arrows_per_end ?? 0);
  return { total, xCount, tens, nines, avgPerArrow: shot ? total / shot : 0 };
}

/**
 * One round rendered as a summary card: heatmap thumbnail on the left,
 * round name / date / face on the right, and a stat strip — total,
 * average per arrow, and X / 10 / 9 counts.
 *
 * Renders entirely from the enriched RoundSummary (#1310): the heatmap reads
 * `shot_positions` and every stat comes off the summary, so neither the full
 * card (Home, Practice) nor the compact card (History) fetches a per-round
 * RoundDetail. `compact` (History) simply hides the 10/9 ring counts.
 */
export function RoundSummaryCard({ round, tz, compact = false }) {
  const s = summaryStats(round);
  const detail =
    round.event_detail ||
    [round.target_face, round.distance_m ? `${round.distance_m}m` : null]
      .filter(Boolean)
      .join(" · ");

  // `scored` (you scored this round for another archer) drives the "Scored" /
  // "for X" context tag below — NOT the thumbnail.
  const scored = round.scored_only;
  // Round was truncated (ended early, #1820): fewer ends than originally
  // planned. Same predicate as the round-detail "ended early" banner — without
  // it a truncated 138 on a 300-round context reads as a terrible complete 300.
  const endedEarly =
    round.originally_planned_ends != null &&
    round.ends_total != null &&
    round.ends_total < round.originally_planned_ends;
  // In-progress open-ended round (no fixed end count, #2428): the chip shows
  // how far the archer has gotten instead of a "N/total" that has no total.
  const openInProgress = round.open_ended === true && round.status === "in_progress";
  // Thumbnail rule: show the target face (+ recorded arrow dots) whenever the
  // round has any recorded scores; the notepad is only for rounds with nothing
  // recorded (opened/scored but no ends entered). A round you scored for someone
  // else still shows its face + dots once it has scores.
  const hasScores = (round.ends_done ?? 0) > 0;

  return (
    <Link to={`/rounds/${round.id}`} className="rsc">
      {!hasScores ? (
        <ScoredThumbnail />
      ) : (
        <ShotHeatmap
          arrows={(round.shot_positions || []).map(([x, y]) => ({ x, y }))}
          face={round.target_face}
        />
      )}
      <div className="rsc-body">
        <div className="rsc-head">
          <div className="rsc-title">{round.event_name}</div>
          {scored && <span className="rsc-scored-tag">Scored</span>}
        </div>
        {/* The variable-length date · context · distance text ellipsizes on
            narrow viewports; the ends chip is integrity information and must
            never be the part that gets cut, so it lives OUTSIDE the ellipsized
            span as a non-shrinking sibling (QA follow-up to #1820). */}
        <div className="rsc-subrow">
          <div className="rsc-sub mono">
            {[
              // The date of a round is `started_at` (#2047). Rendering
              // completed_at left an unfinished round with a BLANK date, and
              // disagreed with the month header History groups it under,
              // which keys off started_at too (#2166).
              formatDayMonthYearUpper(round.started_at, tz),
              scored && round.archer_name ? `for ${round.archer_name}` : detail,
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
          {openInProgress && (
            <span className="rsc-ends-chip mono">{round.ends_done} ends so far</span>
          )}
          {endedEarly && (
            <span className="rsc-ends-chip mono">
              {round.ends_total}/{round.originally_planned_ends} ends
            </span>
          )}
        </div>
        <div className="rsc-stats">
          <div className="rsc-stat">
            <b>{s.total}</b>
            <span>total</span>
          </div>
          <div className="rsc-stat">
            <b>{oneDecimal(s.avgPerArrow)}</b>
            <span>avg/arrow</span>
          </div>
          <div className="rsc-stat">
            <b>{s.xCount}</b>
            <span>Xs</span>
          </div>
          {!compact && (
            <>
              <div className="rsc-stat">
                <b>{s.tens}</b>
                <span>10s</span>
              </div>
              <div className="rsc-stat">
                <b>{s.nines}</b>
                <span>9s</span>
              </div>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}
